import React, {useEffect, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import './GroupSchedulePage.scss';
import {CircularProgress} from '@mui/material';
import get from 'lodash/get';
import {getDataFromParams} from '../../utils/urlUtils';
import GroupSchedulePageTop from './GroupSchedulePageTop/GroupSchedulePageTop';
import {SCHEDULE_FOR_LINK} from '../../constants/links';
import {ScheduleView} from '../ScheduleView/ScheduleView';
import {getScheduleType} from '../../helper/getScheduleType';
import {getAllDepartmentsService} from '../../services/departmentService';
import {useDefaultSemester} from "../../hooks/useSchedule";

const createSubmitValues = (semester, group, teacher, department) => ({
    semester,
    group: {id: group},
    teacher: {id: teacher},
    department: {id: department},
});

const buildScheduleQuery = ({ semesterId, group, teacher, department }) => {
    const params = new URLSearchParams();
    if (semesterId) params.set('semester', semesterId);
    if (get(group, 'id')) params.set('group', group.id);
    if (get(teacher, 'id')) params.set('teacher', teacher.id);
    if (get(department, 'id')) params.set('department', department.id);
    return `${SCHEDULE_FOR_LINK}?${params}`;
};

const GroupSchedulePage = (props) => {
    const history = useHistory();
    const location = useLocation();
    const {t} = useTranslation('common');
    const [viewMode, setViewMode] = useState('today'); // 'all' | 'today'

    const {data: defaultSemester = {}} = useDefaultSemester();

    const {
        scheduleType,
        loading,
        getGroupSchedule,
        getTeacherSchedule,
        getTeacherActiveSemestersSchedule,
        getFullSchedule,
        getDepartmentSchedule,
        groupSchedule,
        teacherSchedule,
        fullSchedule,
    } = props;

    useEffect(() => {
        getAllDepartmentsService();
    }, []);

    useEffect(() => {
        if (!loading && scheduleType) {
            const timer = setTimeout(() => {
                const currentDayElement = document.querySelector('.currentDay');
                if (currentDayElement) {
                    currentDayElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [loading, scheduleType, groupSchedule, teacherSchedule, fullSchedule]);

    const scheduleActions = {
        group: (values) => {
            const {semester, group} = values;
            getGroupSchedule(semester.id, group.id);
        },
        teacher: (values) => {
            const {semester, teacher} = values;
            getTeacherSchedule(semester.id, teacher.id);
        },
        full: (values) => {
            const {semester} = values;
            getFullSchedule(semester.id);
        },
        department: (values) => {
            const {semester, department} = values;
            getDepartmentSchedule(semester.id, department.id);
        },
    };

    const handleSubmit = (values) => {
        const {semester, group, teacher, department} = values;
        history.push(buildScheduleQuery({semesterId: semester?.id, group, teacher, department}));
    };

    const getSchedule = () => {
        const {semester, group, teacher, department} = getDataFromParams(location);
        const semesterValue = semester ? {id: Number(semester)} : defaultSemester;
        const values = createSubmitValues(semesterValue, group, teacher, department);
        const typeOfSchedule = getScheduleType(values);

        if (!semester && typeOfSchedule === 'teacher' && teacher) {
            getTeacherActiveSemestersSchedule(Number(teacher));
            return;
        }

        if (!semester) {
            history.push(buildScheduleQuery({
                semesterId: semesterValue.id,
                group: values.group,
                teacher: values.teacher,
                department: values.department,
            }));
        }

        scheduleActions[typeOfSchedule](values);
    };

    useEffect(() => {

        if (defaultSemester.id) {
            getSchedule();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultSemester, location.search]);

    // Teachers reach this page through "my schedule", but the filters are as useful to them as to
    // anyone — every dataset behind them, and every schedule they can ask for, is open to a
    // teacher. Only an archived schedule has nothing to filter.
    const getTop = () =>
        scheduleType !== 'archived' && (
            <GroupSchedulePageTop
                scheduleType={scheduleType}
                handleSubmit={handleSubmit}
                isManager={props.isManager}
            />
        );

    return (
        <div className="schedule_page-container">
            {getTop()}
            {loading ? (
                <section className="centered-container">
                    <CircularProgress/>
                </section>
            ) : (
                ScheduleView({...props, t, viewMode, setViewMode})
            )}
        </div>
    );
};

export default GroupSchedulePage;